import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CheckCircle,
  Groups2,
  HelpOutline,
  Hub,
  TouchApp,
} from "@mui/icons-material";

import RadialHub from "../components/home/RadialHub";
import "../styles/landing.css";

const statCards = [
  {
    key: "seekinghelp",
    label: "Seeking Help",
    icon: <Groups2 />,
    fallback: 0,
  },
  {
    key: "outstandingquestions",
    label: "Questions",
    icon: <HelpOutline />,
    fallback: 0,
  },
  {
    key: "connections",
    label: "Connections",
    icon: <Hub />,
    fallback: 0,
  },
];

const tutorialSteps = [
  {
    eyebrow: "Start here",
    title: "Home",
    body: "Use Home to reach the main Udyog Connect dashboard after you enter the community experience.",
    selector: "[data-tour='nav-home']",
    placement: "bottom",
  },
  {
    eyebrow: "Organization",
    title: "Enterprise Onboarding",
    body: "Use Enterprise Onboarding when an organization wants to join, support programs, or be reviewed by admins.",
    selector: "[data-tour='nav-enterprise-onboarding']",
    placement: "bottom",
  },
  {
    eyebrow: "Community",
    title: "About",
    body: "About explains the mission, vision, goals, and story behind the Udyog Connect community.",
    selector: "[data-tour='nav-about']",
    placement: "bottom",
  },
  {
    eyebrow: "Learning",
    title: "Training Programs",
    body: "Training Programs highlights partnership-led learning paths and community skill-building programs.",
    selector: "[data-tour='nav-training']",
    placement: "bottom",
  },
  {
    eyebrow: "Partners",
    title: "Our Partners",
    body: "Our Partners shows the organizations supporting Udyog Connect programs and community outcomes.",
    selector: "[data-tour='nav-partners']",
    placement: "bottom",
  },
  {
    eyebrow: "Schedule",
    title: "Calendar",
    body: "Calendar shows Udyog Connect events and training programs in one place, with details and apply links where available.",
    selector: "[data-tour='nav-calendar']",
    placement: "bottom",
  },
  {
    eyebrow: "Career",
    title: "Opportunities",
    body: "Opportunities lets members discover roles, apply, edit applications, and continue conversations with providers.",
    selector: "[data-tour='nav-opportunities']",
    placement: "bottom",
  },
  {
    eyebrow: "Support",
    title: "Request",
    body: "Request is where users ask providers for help or send feedback and issues to moderators.",
    selector: "[data-tour='nav-request']",
    placement: "bottom",
  },
  {
    eyebrow: "More",
    title: "Explore",
    body: "Explore opens more options such as Events, Industry Presence, Udyog Connect entry, and Tech Summit pages.",
    selector: "[data-tour='nav-explore']",
    placement: "bottom",
  },
  {
    eyebrow: "Industries",
    title: "Industry Hub",
    body: "Click any node in the circular hub to view industry-specific providers, opportunities, and request guidance.",
    selector: "[data-tour='landing-hub']",
    placement: "top",
  },
  {
    eyebrow: "Community pulse",
    title: "Live Stats",
    body: "This panel shows activity such as users seeking help, questions, and community connections.",
    selector: "[data-tour='landing-stats']",
    placement: "right",
  },
  {
    eyebrow: "Progress",
    title: "Achievements",
    body: "Achievements show community progress and milestones so users can understand impact over time.",
    selector: "[data-tour='landing-achievements']",
    placement: "left",
  },
  {
    eyebrow: "Account",
    title: "Profile Menu",
    body: "After login, use the profile icon to update your profile, change your password, or logout.",
    selector: "[data-tour='nav-profile'], [data-tour='nav-login']",
    placement: "bottom",
  },
];

const LandingPage = () => {
  const [stats, setStats] = useState({});
  const [leaders, setLeaders] = useState([]);
  const [accomplishments, setAccomplishments] = useState([]);
  const [status, setStatus] = useState("loading");
  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem("udyog2LandingTutorialSeen") !== "yes";
  });
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialTarget, setTutorialTarget] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setStatus("loading");

      const [dyn, leadersRes, acc] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getdynamicdata`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getstackholder_list`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getaccomplishmnet`),
      ]);

      if (!mounted) return;

      if (dyn.status === "fulfilled" && Array.isArray(dyn.value.data)) {
        const mapped = dyn.value.data.reduce((a, c) => {
          a[c.fieldname] = c.fieldvalue;
          return a;
        }, {});
        setStats(mapped);
      }

      if (leadersRes.status === "fulfilled") {
        const leaderData = leadersRes.value.data?.leaders;
        setLeaders(Array.isArray(leaderData) ? leaderData : []);
      }

      if (acc.status === "fulfilled") {
        setAccomplishments(Array.isArray(acc.value.data) ? acc.value.data : []);
      }

      setStatus(
        [dyn, leadersRes, acc].some((result) => result.status === "fulfilled")
          ? "ready"
          : "error"
      );
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const primaryAccomplishments = useMemo(
    () => accomplishments.slice(0, 5),
    [accomplishments]
  );

  const closeTutorial = () => {
    localStorage.setItem("udyog2LandingTutorialSeen", "yes");
    setShowTutorial(false);
    setTutorialStep(0);
  };

  const openTutorial = () => {
    setTutorialStep(0);
    setShowTutorial(true);
  };

  const activeTutorial = tutorialSteps[tutorialStep];
  const isLastTutorialStep = tutorialStep === tutorialSteps.length - 1;

  useEffect(() => {
    if (!showTutorial) return undefined;

    const updateTarget = () => {
      if (
        activeTutorial.selector.includes("nav-") &&
        !activeTutorial.selector.includes("nav-profile") &&
        window.innerWidth <= 768
      ) {
        window.dispatchEvent(new Event("udyog2-open-tour-menu"));
      }

      const target = document.querySelector(activeTutorial.selector);

      if (!target) {
        setTutorialTarget(null);
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });

      window.setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const isVisible =
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < window.innerHeight &&
          rect.left < window.innerWidth;

        setTutorialTarget(isVisible ? rect : null);
      }, 180);
    };

    updateTarget();
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [activeTutorial, showTutorial]);

  const getCalloutStyle = () => {
    if (!tutorialTarget) return {};

    const gap = 16;
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const targetCenter = tutorialTarget.left + tutorialTarget.width / 2;
    const left = Math.min(
      Math.max(16, targetCenter - cardWidth / 2),
      window.innerWidth - cardWidth - 16
    );

    if (activeTutorial.placement === "top") {
      return {
        width: cardWidth,
        left,
        top: Math.max(16, tutorialTarget.top - gap),
        transform: "translateY(-100%)",
      };
    }

    if (activeTutorial.placement === "left") {
      return {
        width: cardWidth,
        left: Math.max(16, tutorialTarget.left - cardWidth - gap),
        top: Math.min(Math.max(16, tutorialTarget.top), window.innerHeight - 260),
      };
    }

    if (activeTutorial.placement === "right") {
      return {
        width: cardWidth,
        left: Math.min(tutorialTarget.right + gap, window.innerWidth - cardWidth - 16),
        top: Math.min(Math.max(16, tutorialTarget.top), window.innerHeight - 260),
      };
    }

    return {
      width: cardWidth,
      left,
      top: Math.min(tutorialTarget.bottom + gap, window.innerHeight - 260),
    };
  };

  return (
    <main className="landing">
      {showTutorial && (
        <div className="landing-tour-layer" role="dialog" aria-modal="true" aria-labelledby="landing-tutorial-title">
          {tutorialTarget && (
            <div
              className="landing-tour-highlight"
              style={{
                left: tutorialTarget.left - 8,
                top: tutorialTarget.top - 8,
                width: tutorialTarget.width + 16,
                height: tutorialTarget.height + 16,
              }}
            />
          )}
          <div
            className={`landing-tutorial-card tour-callout ${tutorialTarget ? "anchored" : "centered"} placement-${activeTutorial.placement}`}
            style={getCalloutStyle()}
          >
            <button
              className="tutorial-close-btn"
              type="button"
              onClick={closeTutorial}
              aria-label="Skip tutorial"
            >
              x
            </button>

            <div className="tutorial-step-count">
              Step {tutorialStep + 1} of {tutorialSteps.length}
            </div>
            <span className="landing-kicker">{activeTutorial.eyebrow}</span>
            <h2 id="landing-tutorial-title">{activeTutorial.title}</h2>
            <p>{activeTutorial.body}</p>

            <div className="tutorial-progress">
              {tutorialSteps.map((step, index) => (
                <button
                  key={step.title}
                  type="button"
                  className={`tutorial-dot ${index === tutorialStep ? "active" : ""}`}
                  onClick={() => setTutorialStep(index)}
                  aria-label={`Go to tutorial step ${index + 1}`}
                />
              ))}
            </div>

            <div className="tutorial-actions">
              <button
                type="button"
                className="tutorial-secondary-btn"
                onClick={closeTutorial}
              >
                Skip
              </button>
              <div className="tutorial-nav-actions">
                <button
                  type="button"
                  className="tutorial-secondary-btn"
                  disabled={tutorialStep === 0}
                  onClick={() => setTutorialStep((step) => Math.max(step - 1, 0))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="tutorial-primary-btn"
                  onClick={() => {
                    if (isLastTutorialStep) {
                      closeTutorial();
                    } else {
                      setTutorialStep((step) => step + 1);
                    }
                  }}
                >
                  {isLastTutorialStep ? "Start Exploring" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="landing-alert">
          Live landing data is temporarily unavailable. The industry hub is
          still ready to use.
        </div>
      )}

      <section className="landing-grid" aria-label="Udyog Connect overview">
        <aside className="landing-side-card stats-card" data-tour="landing-stats">
          <div className="landing-card-header">
            <span className="landing-kicker">Live pulse</span>
            <h2>Community Stats</h2>
          </div>

          <div className="landing-stat-list">
            {statCards.map((item) => (
              <div className="landing-stat-item" key={item.key}>
                <span className="landing-stat-icon">{item.icon}</span>
                <span>
                  <strong>{stats[item.key] || item.fallback}</strong>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </aside>

        <section className="hub-stage" aria-label="Industry hub" data-tour="landing-hub">
          <div className="hub-guide">
            <TouchApp fontSize="small" />
            <span>Click any industry node for more industry details</span>
            <button type="button" className="hub-guide-btn" onClick={openTutorial}>
              Guide
            </button>
          </div>
          <RadialHub />
        </section>

        <aside className="landing-side-card achievements-card" data-tour="landing-achievements">
          <div className="landing-card-header">
            <span className="landing-kicker">Progress</span>
            <h2>Achievements</h2>
          </div>

          <div className="achievement-list">
            {status === "loading" &&
              Array.from({ length: 4 }).map((_, i) => (
                <div className="landing-skeleton achievement-skeleton" key={i} />
              ))}

            {status !== "loading" &&
              primaryAccomplishments.map((a, i) => (
                <div className="achievement-item" key={a.id || i}>
                  <CheckCircle fontSize="small" />
                  <p>{a.description}</p>
                </div>
              ))}

            {status !== "loading" && primaryAccomplishments.length === 0 && (
              <div className="landing-empty-state">
                Achievements will appear here soon.
              </div>
            )}
          </div>
        </aside>
      </section>

      <section className="founders">
        <div className="founders-header">
          <span className="landing-kicker">Leadership</span>
          <h2>Our Founders</h2>
          <p>Visionaries driving Udyog Connect forward.</p>
        </div>

        <div className="founders-grid">
          {status === "loading" &&
            Array.from({ length: 3 }).map((_, i) => (
              <div className="founder-card-premium landing-skeleton" key={i} />
            ))}

          {status !== "loading" &&
            leaders.map((l, i) => (
              <article key={l.id || l.name || i} className="founder-card-premium">
                <div className="founder-img-wrap">
                  <img src={l.img} alt={l.name || "Founder"} />
                  <div className="founder-overlay" />
                </div>

                <div className="founder-content">
                  <h3>{l.name}</h3>
                  <span>{l.title}</span>
                </div>
              </article>
            ))}

          {status !== "loading" && leaders.length === 0 && (
            <div className="landing-empty-state founders-empty">
              Founder profiles will appear here soon.
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
